import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { X, Send, Sparkles, Calendar, ArrowLeft, Loader2 } from 'lucide-react';

// Calendly configuration
const CALENDLY_CONFIG = {
  url: 'https://calendly.com/felix-optimumvertexcapital', // Your actual Calendly URL
  types: {
    'consultation': '30-minute consultation',
    'coffee': '15-minute coffee chat',
    'project': '1-hour project discussion',
    'mentoring': '45-minute mentoring session'
  }
};

// LLM Configuration
const LLM_CONFIG = {
  enabled: true, // Set to false to use demo responses
  provider: 'openrouter', // 'ollama', 'together', 'huggingface', 'openrouter'
  endpoints: {
    ollama: 'http://localhost:11434/api/generate',
    together: 'https://api.together.xyz/v1/chat/completions',
    huggingface: 'https://api-inference.huggingface.co/models/meta-llama/Llama-2-7b-chat-hf',
    openrouter: 'https://openrouter.ai/api/v1/chat/completions'
  },
  models: {
    ollama: 'llama2:7b',
    together: 'meta-llama/Llama-2-70b-chat',
    huggingface: 'meta-llama/Llama-2-7b-chat-hf',
    openrouter: 'meta-llama/llama-3.1-8b-instruct' // Default model
  },
  apiKeys: {
    together: '', // Add your Together AI API key here
    huggingface: '', // Add your Hugging Face API key here
    openrouter: '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'
  }
};

// Calendly Widget Component
const CalendlyWidget: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  useEffect(() => {
    // Load Calendly script if not already loaded
    if (!window.Calendly) {
      const script = document.createElement('script');
      script.src = 'https://assets.calendly.com/assets/external/widget.js';
      script.async = true;
      document.head.appendChild(script);
    }

    // Initialize Calendly widget when script loads
    const initWidget = () => {
      if (window.Calendly) {
        window.Calendly.initInlineWidget({
          url: CALENDLY_CONFIG.url,
          parentElement: document.getElementById('calendly-widget-container'),
          prefill: {},
          utm: {}
        });
      }
    };

    // Check if Calendly is already loaded
    if (window.Calendly) {
      initWidget();
    } else {
      // Wait for script to load
      const checkCalendly = setInterval(() => {
        if (window.Calendly) {
          clearInterval(checkCalendly);
          initWidget();
        }
      }, 100);
    }

    return () => {
      // Cleanup
      const container = document.getElementById('calendly-widget-container');
      if (container) {
        container.innerHTML = '';
      }
    };
  }, []);

  return (
    <div className="absolute inset-0 bg-white dark:bg-gray-900 z-10 flex flex-col">
      {/* Header */}
      <div className="flex items-center gap-2 sm:gap-3 px-4 sm:px-6 py-4 bg-gradient-to-r from-green-600 to-green-700 text-white">
        <button 
          onClick={onClose}
          className="text-white/80 hover:text-white transition-colors"
          aria-label="Back to chat"
        >
          <ArrowLeft size={18} className="sm:w-5 sm:h-5" />
        </button>
        <div className="flex items-center gap-2">
          <Calendar size={18} className="sm:w-5 sm:h-5" />
          <span className="font-semibold text-sm sm:text-base">Schedule a Meeting</span>
        </div>
      </div>
      
      {/* Calendly Widget Container */}
      <div 
        id="calendly-widget-container" 
        className="flex-1 min-h-0"
        style={{ minHeight: '400px' }}
      />
    </div>
  );
};

// Add Calendly to window object type
declare global {
  interface Window {
    Calendly?: {
      initInlineWidget: (config: any) => void;
    };
  }
}

// LLM Service
class LLMService {
  private static instance: LLMService;
  
  static getInstance(): LLMService {
    if (!LLMService.instance) {
      LLMService.instance = new LLMService();
    }
    return LLMService.instance;
  }

  async generateResponse(message: string, context: string): Promise<string> {
    if (!LLM_CONFIG.enabled) {
      return this.getDemoResponse(message);
    }

    try {
      switch (LLM_CONFIG.provider) {
        case 'ollama':
          return await this.callOllama(message, context);
        case 'together':
          return await this.callTogetherAI(message, context);
        case 'huggingface':
          return await this.callHuggingFace(message, context);
        case 'openrouter':
          return await this.callOpenRouter(message, context);
        default:
          return this.getDemoResponse(message);
      }
    } catch (error) {
      console.error('LLM API error:', error);
      return this.getDemoResponse(message);
    }
  }

  private async callOllama(message: string, context: string): Promise<string> {
    const response = await fetch(LLM_CONFIG.endpoints.ollama, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        model: LLM_CONFIG.models.ollama,
        prompt: this.buildPrompt(message, context),
        stream: false,
        options: {
          temperature: 0.7,
          top_p: 0.9,
          max_tokens: 500
        }
      })
    });

    if (!response.ok) throw new Error('Ollama API error');
    
    const data = await response.json();
    return data.response || 'I apologize, but I couldn\'t generate a response.';
  }

  private async callTogetherAI(message: string, context: string): Promise<string> {
    if (!LLM_CONFIG.apiKeys.together) {
      throw new Error('Together AI API key not configured');
    }

    const response = await fetch(LLM_CONFIG.endpoints.together, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${LLM_CONFIG.apiKeys.together}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        model: LLM_CONFIG.models.together,
        messages: [
          {
            role: 'system',
            content: this.buildSystemPrompt(context)
          },
          {
            role: 'user',
            content: message
          }
        ],
        max_tokens: 500,
        temperature: 0.7,
        top_p: 0.9
      })
    });

    if (!response.ok) throw new Error('Together AI API error');
    
    const data = await response.json();
    return data.choices?.[0]?.message?.content || 'I apologize, but I couldn\'t generate a response.';
  }

  private async callHuggingFace(message: string, context: string): Promise<string> {
    if (!LLM_CONFIG.apiKeys.huggingface) {
      throw new Error('Hugging Face API key not configured');
    }

    const response = await fetch(LLM_CONFIG.endpoints.huggingface, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${LLM_CONFIG.apiKeys.huggingface}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        inputs: this.buildPrompt(message, context),
        parameters: {
          max_new_tokens: 500,
          temperature: 0.7,
          top_p: 0.9
        }
      })
    });

    if (!response.ok) throw new Error('Hugging Face API error');
    
    const data = await response.json();
    return data[0]?.generated_text || 'I apologize, but I couldn\'t generate a response.';
  }

  private async callOpenRouter(message: string, context: string): Promise<string> {
    if (!LLM_CONFIG.apiKeys.openrouter) {
      throw new Error('OpenRouter API key not configured');
    }

    const response = await fetch(LLM_CONFIG.endpoints.openrouter, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${LLM_CONFIG.apiKeys.openrouter}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        model: LLM_CONFIG.models.openrouter,
        messages: [
          {
            role: 'system',
            content: this.buildSystemPrompt(context)
          },
          {
            role: 'user',
            content: message
          }
        ],
        max_tokens: 500,
        temperature: 0.7,
        top_p: 0.9
      })
    });

    if (!response.ok) throw new Error('OpenRouter API error');
    
    const data = await response.json();
    return data.choices?.[0]?.message?.content || 'I apologize, but I couldn\'t generate a response.';
  }

  private buildSystemPrompt(context: string): string {
    return `You are Felix Amoruwa's AI assistant. Felix is a Product Leader, Adjunct Faculty, Pilot, Realtor®, and Investor/Day Trader based in San Francisco Bay Area.

Current context: ${context}

Key information about Felix:
- Email: famoruwa@berkeley.edu
- Website: https://www.felixamoruwa.info
- Location: San Francisco Bay Area, CA
- LinkedIn: https://www.linkedin.com/in/felixamoruwa
- Twitter: https://twitter.com/Felix_Amoruwa
- GitHub: https://github.com/famoruwa
- Calendly: https://calendly.com/felix-optimumvertexcapital

Felix's expertise:
- Product Management & Leadership
- Software Engineering & Architecture
- AI/ML and Computational Engineering
- Aviation Operations
- Real Estate Investment
- Day Trading & Financial Markets

Education:
- MBA from Carnegie Mellon University
- MSc Software Management from CMU
- BSc Computational Engineering Science from UC Berkeley

Be helpful, professional, and accurate. If asked about scheduling, always mention his Calendly link. Keep responses concise but informative.`;
  }

  private buildPrompt(message: string, context: string): string {
    return `${this.buildSystemPrompt(context)}

User: ${message}
Assistant:`;
  }

  private getDemoResponse(message: string): string {
    // Fallback to the existing demo responses
    return generateResponse(message);
  }
}

const getTabContext = (pathname: string) => {
  if (pathname.startsWith('/experience')) {
    return {
      initial: `👋 Welcome to his Experience tab! I can answer questions about Felix's work history, roles, and achievements. What would you like to know?`,
      actions: [
        { label: 'Show experience', value: "Tell me about Felix's experience." },
        { label: 'Key achievements', value: 'What are some of Felix\'s key achievements?' },
        { label: 'Career timeline', value: 'Show me the career timeline.' },
      ]
    };
  }
  if (pathname.startsWith('/portfolio')) {
    return {
      initial: `👋 Welcome to his Portfolio tab! I can help you explore Felix's projects, technologies, and highlights.`,
      actions: [
        { label: 'Featured projects', value: 'Show me some featured projects.' },
        { label: 'Tech stack', value: 'What technologies does Felix use?' },
        { label: 'Project details', value: 'Tell me more about a specific project.' },
      ]
    };
  }
  if (pathname.startsWith('/skills')) {
    return {
      initial: `👋 Welcome to his Skills tab! I can tell you about Felix's technical skills, strengths, and learning interests.`,
      actions: [
        { label: 'Top skills', value: "What are Felix's top skills?" },
        { label: 'Learning', value: 'What is Felix currently learning?' },
        { label: 'Skill categories', value: 'Show me skill categories.' },
      ]
    };
  }
  if (pathname.startsWith('/conferences')) {
    return {
      initial: `👋 Welcome to his Conferences tab! I can share info about Felix's speaking engagements, topics, and events.`,
      actions: [
        { label: 'Speaking topics', value: 'What topics has Felix spoken about?' },
        { label: 'Event highlights', value: 'Show me conference highlights.' },
        { label: 'Upcoming talks', value: 'Any upcoming speaking events?' },
      ]
    };
  }
  if (pathname.startsWith('/contact')) {
    return {
      initial: `👋 Welcome to his Contact tab! I can help you get in touch with Felix, schedule a meeting, or answer questions about reaching out.`,
      actions: [
        { label: 'Contact info', value: 'How can I contact Felix?' },
        { label: 'Social links', value: 'Show me Felix\'s social media.' },
        { label: 'Send a message', value: 'How do I send a message?' },
      ]
    };
  }
  // Home or default
  return {
    initial: `👋 Hello! I'm the virtual assistant for Felix's personal page. I can help you explore this site:\n\n• Learn about Felix's experience and background\n• Discover featured projects and skills\n• Get contact information and schedule meetings\n• Navigate to different sections\n\nWhat would you like to know or explore?`,
    actions: [
      { label: 'Show experience', value: "Tell me about Felix's experience." },
      { label: 'Featured projects', value: 'Show me some featured projects.' },
      { label: 'Skills', value: "What are Felix's top skills?" },
      { label: 'Contact info', value: 'How can I contact Felix?' },
    ]
  };
};

const generateResponse = (message: string): string => {
  const lowerMessage = message.toLowerCase();
  
  // Contact and scheduling related responses
  if (lowerMessage.includes('schedule') || lowerMessage.includes('meeting') || lowerMessage.includes('appointment') || lowerMessage.includes('book')) {
    return `Great! I can help you schedule a meeting with Felix. Here are his available meeting types:\n\n` +
           `📅 **Meeting Options:**\n` +
           `• ${CALENDLY_CONFIG.types.consultation} - Perfect for initial discussions\n` +
           `• ${CALENDLY_CONFIG.types.coffee} - Quick chat or networking\n` +
           `• ${CALENDLY_CONFIG.types.project} - Detailed project discussions\n` +
           `• ${CALENDLY_CONFIG.types.mentoring} - Career guidance and mentoring\n\n` +
           `You can book directly through his Calendly: ${CALENDLY_CONFIG.url}\n\n` +
           `Would you like me to help you with anything specific about scheduling?`;
  }
  
  if (lowerMessage.includes('contact') || lowerMessage.includes('email') || lowerMessage.includes('reach')) {
    return `Here's how you can get in touch with Felix:\n\n` +
           `📧 **Email:** famoruwa@berkeley.edu\n` +
           `🌐 **Website:** https://www.felixamoruwa.info\n` +
           `📍 **Location:** San Francisco Bay Area, CA\n\n` +
           `📱 **Social Media:**\n` +
           `• LinkedIn: https://www.linkedin.com/in/felixamoruwa\n` +
           `• Twitter: https://twitter.com/Felix_Amoruwa\n` +
           `• GitHub: https://github.com/famoruwa\n\n` +
           `💡 **Pro tip:** For the fastest response, try scheduling a meeting through his Calendly!`;
  }
  
  if (lowerMessage.includes('social') || lowerMessage.includes('linkedin') || lowerMessage.includes('twitter') || lowerMessage.includes('github')) {
    return `Here are Felix's social media profiles:\n\n` +
           `🔗 **LinkedIn:** https://www.linkedin.com/in/felixamoruwa\n` +
           `🐦 **Twitter:** https://twitter.com/Felix_Amoruwa\n` +
           `💻 **GitHub:** https://github.com/famoruwa\n` +
           `📰 **Hacker News:** https://news.ycombinator.com/user?id=oamoruwa\n\n` +
           `Feel free to connect with him on any of these platforms!`;
  }
  
  // Experience related responses
  if (lowerMessage.includes('experience') || lowerMessage.includes('work') || lowerMessage.includes('career')) {
    return `Felix has an impressive career spanning multiple industries:\n\n` +
           `🎯 **Current Role:** Product Leader, Adjunct Faculty, Pilot, Realtor®, Investor/Day Trader\n\n` +
           `🏢 **Key Experience Areas:**\n` +
           `• Product Management & Leadership\n` +
           `• Software Engineering & Architecture\n` +
           `• Aviation & Flight Operations\n` +
           `• Real Estate & Investment\n` +
           `• Academic Teaching & Mentoring\n\n` +
           `📚 **Education:**\n` +
           `• MBA from Carnegie Mellon University\n` +
           `• MSc Software Management from CMU\n` +
           `• BSc Computational Engineering Science from UC Berkeley\n\n` +
           `Would you like to know more about any specific aspect of his experience?`;
  }
  
  // Skills related responses
  if (lowerMessage.includes('skill') || lowerMessage.includes('technology') || lowerMessage.includes('tech')) {
    return `Felix has expertise in several cutting-edge domains:\n\n` +
           `🧠 **Technical Skills:**\n` +
           `• Computational Engineering Science\n` +
           `• Artificial Intelligence & Machine Learning\n` +
           `• Software Architecture & Development\n` +
           `• Data Structures & Mining\n` +
           `• Electrical & Computer Engineering\n\n` +
           `📊 **Business Skills:**\n` +
           `• Product Management\n` +
           `• Financial Analysis\n` +
           `• Strategic Planning\n` +
           `• Team Leadership\n\n` +
           `✈️ **Specialized Skills:**\n` +
           `• Aviation Operations\n` +
           `• Real Estate Investment\n` +
           `• Day Trading & Financial Markets\n\n` +
           `What specific skills are you interested in learning more about?`;
  }
  
  // Projects related responses
  if (lowerMessage.includes('project') || lowerMessage.includes('portfolio') || lowerMessage.includes('work')) {
    return `Felix has worked on various interesting projects across different domains:\n\n` +
           `💼 **Professional Projects:**\n` +
           `• Product leadership roles at major tech companies\n` +
           `• Software architecture and development\n` +
           `• AI/ML implementations\n\n` +
           `🎓 **Academic Projects:**\n` +
           `• Research in computational engineering\n` +
           `• Financial modeling and analysis\n` +
           `• Software management systems\n\n` +
           `🚁 **Aviation Projects:**\n` +
           `• Flight operations and training\n` +
           `• Drone technology applications\n\n` +
           `Would you like to explore any specific project area in more detail?`;
  }
  
  // Default response
  return `I'm here to help you learn more about Felix! You can ask me about:\n\n` +
         `• His experience and background\n` +
         `• Skills and expertise\n` +
         `• Projects and portfolio\n` +
         `• Contact information and scheduling\n` +
         `• Social media and networking\n\n` +
         `What would you like to know?`;
};

const ChatAssistant: React.FC = () => {
  const location = useLocation();
  const tabContext = getTabContext(location.pathname);
  const [open, setOpen] = useState(true);
  const [messages, setMessages] = useState([{ sender: 'assistant', text: tabContext.initial }]);
  const [input, setInput] = useState('');
  const [showCalendly, setShowCalendly] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const prevPathRef = useRef(location.pathname);
  const llmService = LLMService.getInstance();

  useEffect(() => {
    if (open && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, open]);

  useEffect(() => {
    if (!open) {
      setMessages([{ sender: 'assistant', text: tabContext.initial }]);
    } else if (open && location.pathname !== prevPathRef.current) {
      // Only append a new greeting if the route changed while chat is open
      setMessages((msgs) => [
        ...msgs,
        { sender: 'assistant', text: tabContext.initial }
      ]);
    }
    prevPathRef.current = location.pathname;
  }, [location.pathname, open, tabContext.initial]);

  const handleSend = async (msg?: string) => {
    const text = msg ?? input;
    if (!text.trim()) return;
    
    const userMsg = { sender: 'user', text };
    setMessages((msgs) => [...msgs, userMsg]);
    setInput('');
    setIsLoading(true);
    
    try {
      // Generate response using LLM or fallback
      const response = await llmService.generateResponse(text, tabContext.initial);
      setMessages((msgs) => [
        ...msgs,
        { sender: 'assistant', text: response }
      ]);
    } catch (error) {
      console.error('Error generating response:', error);
      // Fallback to demo response
      const fallbackResponse = generateResponse(text);
      setMessages((msgs) => [
        ...msgs,
        { sender: 'assistant', text: fallbackResponse }
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCalendlyClick = () => {
    setShowCalendly(true);
    handleSend('I want to schedule a meeting');
  };

  return (
    <>
      {/* Floating Chat Button */}
      {!open && (
        <button
          className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50 bg-gradient-to-tr from-primary-600 to-purple-500 hover:from-primary-700 hover:to-purple-600 text-white rounded-full shadow-xl p-3 sm:p-4 flex items-center justify-center transition-colors"
          onClick={() => setOpen(true)}
          aria-label="Open chat assistant"
        >
          <Sparkles size={24} className="sm:w-7 sm:h-7" />
        </button>
      )}

      {/* Overlay */}
      {open && (
        <div className="fixed inset-0 z-40 bg-black/10 backdrop-blur-sm" onClick={() => setOpen(false)} />
      )}

      {/* Sliding Chat Panel */}
      <div
        className={`fixed top-0 right-0 h-full w-full sm:max-w-md bg-white dark:bg-gray-900 shadow-2xl z-50 flex flex-col transition-transform duration-300 ${open ? 'translate-x-0' : 'translate-x-full'} sm:rounded-l-2xl border-l`}
        style={{ boxShadow: open ? 'rgba(80,0,120,0.15) -8px 0px 32px' : undefined }}
      >
        {/* Calendly Widget Overlay */}
        {showCalendly && (
          <CalendlyWidget onClose={() => setShowCalendly(false)} />
        )}

        {/* Header */}
        <div className="flex items-center gap-3 px-4 sm:px-6 py-4 bg-gradient-to-r from-[#38bdf8] via-[#0ea5e9] to-[#0369a1] text-white sm:rounded-tl-2xl">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-full bg-white/20 flex items-center justify-center shadow">
              <Sparkles size={20} className="sm:w-6 sm:h-6" />
            </div>
            <div>
              <div className="font-semibold text-sm sm:text-base leading-tight">Felix's Assistant</div>
              <div className="text-xs opacity-80">Online</div>
            </div>
          </div>
          <button className="ml-auto text-white/80 hover:text-white" onClick={() => setOpen(false)} aria-label="Close chat">
            <X size={20} className="sm:w-6 sm:h-6" />
          </button>
        </div>
        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 space-y-3 bg-gradient-to-br from-[#e0f2fe] via-[#f0f9ff] to-[#bae6fd] dark:from-[#0f172a] dark:via-[#1e293b] dark:to-[#0369a1]">
          {messages.map((msg, idx) => (
            <div
              key={idx}
              className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`rounded-2xl px-3 sm:px-4 py-2 max-w-[85%] sm:max-w-[80%] text-xs sm:text-sm shadow-sm whitespace-pre-line ${
                  msg.sender === 'user'
                    ? 'bg-primary-600 text-white rounded-br-md'
                    : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-bl-md border'
                }`}
              >
                {msg.text}
              </div>
            </div>
          ))}
          
          {/* Loading indicator */}
          {isLoading && (
            <div className="flex justify-start">
              <div className="rounded-2xl px-3 sm:px-4 py-2 max-w-[85%] sm:max-w-[80%] text-xs sm:text-sm shadow-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-bl-md border">
                <div className="flex items-center gap-2">
                  <Loader2 size={16} className="animate-spin" />
                  <span>Thinking...</span>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>
        {/* Input & Quick Actions */}
        <div className="p-3 sm:p-4 border-t bg-white dark:bg-gray-900">
          <form
            className="flex items-center gap-2 mb-3"
            onSubmit={e => {
              e.preventDefault();
              handleSend();
            }}
          >
            <input
              type="text"
              className="flex-1 rounded-full border px-3 sm:px-4 py-2 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-primary-400 dark:bg-gray-800 dark:text-white shadow"
              placeholder="Ask me anything..."
              value={input}
              onChange={e => setInput(e.target.value)}
              autoFocus={open}
            />
            <button
              type="submit"
              className="bg-gradient-to-tr from-primary-600 to-purple-500 hover:from-primary-700 hover:to-purple-600 text-white rounded-full p-2 flex items-center justify-center transition-colors shadow"
              aria-label="Send message"
            >
              <Send size={16} className="sm:w-5 sm:h-5" />
            </button>
          </form>
          <div className="flex flex-wrap gap-1 sm:gap-2">
            {tabContext.actions.map((action, idx) => (
              <button
                key={idx}
                className="bg-primary-50 hover:bg-primary-100 text-primary-700 text-xs px-2 sm:px-3 py-1 rounded-full border border-primary-200 transition-colors"
                onClick={() => handleSend(action.value)}
                type="button"
              >
                {action.label}
              </button>
            ))}
            {/* Calendly Quick Action - Available on all pages */}
            <button
              className="bg-green-50 hover:bg-green-100 text-green-700 text-xs px-2 sm:px-3 py-1 rounded-full border border-green-200 transition-colors flex items-center gap-1"
              onClick={handleCalendlyClick}
              type="button"
            >
              <Calendar size={10} className="sm:w-3 sm:h-3" />
              <span className="hidden sm:inline">Book Meeting</span>
              <span className="sm:hidden">Book</span>
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default ChatAssistant; 